import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Search, Users, MapPin, Sparkles, Eye, RefreshCw, Play, Pause, 
  RotateCcw, ZoomIn, ZoomOut, Settings, Filter, Grid, Network,
  Download, Upload, Maximize2, Move3D, MousePointer2, Hand, RotateCw,
  RotateCw as Rotate3D, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronUp,
  ChevronDown, ChevronLeft, ChevronRight, Languages, X, Minimize2,
  Info, BarChart3, BookOpen, Lightbulb, Target, Brain
} from 'lucide-react';
import a2aChineseService from '../api/services/a2aChineseService';
import chineseDataService from '../services/chineseDataService';
import KnowledgeGraphVisualization from './KnowledgeGraphVisualization';
import KnowledgeGraphToolbox from './KnowledgeGraphToolbox';
import EntityDetailsWindow from './EntityDetailsWindow';
import AnalysisStatusModal from './AnalysisStatusModal';
import deepseekEssayApi from '../services/deepseekEssayApi';

const KnowledgeGraph = () => {
  // State management for the integrated knowledge graph analyzer
  const [language, setLanguage] = useState('traditional');
  const [inputText, setInputText] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('graph');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [useMockData, setUseMockData] = useState(true);
  const [selectedSample, setSelectedSample] = useState('moonAndRabbit');
  const [sampleTexts, setSampleTexts] = useState({ sampleList: [], sampleTexts: {} });
  const [loadingSamples, setLoadingSamples] = useState(true);
  
  // Analysis status state
  const [analysisStatus, setAnalysisStatus] = useState({
    timeElapsed: 0,
    tokensUsed: { input: 0, output: 0, total: 0 },
    cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: false, model: 'deepseek-chat' },
    phase: 'preparing',
    message: '準備分析...'
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  // Graph visualization state
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [graphSettings, setGraphSettings] = useState({
    nodeSize: 10,
    linkDistance: 100,
    charge: -300,
    gravity: 0.1,
    friction: 0.9,
    showLabels: true,
    showRelationshipLabels: true,
    animate: true,
    layout: 'force'
  });
  const [filterSettings, setFilterSettings] = useState({
    characters: true,
    places: true,
    objects: true,
    relationships: true
  });

  // Handler functions for filter and display settings
  const handleFilterChange = (filterType, value) => {
    setFilterSettings(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleGraphSettingChange = (settingType, value) => {
    setGraphSettings(prev => ({
      ...prev,
      [settingType]: value
    }));
  };
  
  // 3D visualization state
  // Base zoom has been increased so that visual 100% corresponds to previous 150%
  const [zoom, setZoom] = useState(1.5);
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [translation, setTranslation] = useState({ x: 0, y: 0 });
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const autoRotateRef = useRef(null);
  const [aiOutput, setAiOutput] = useState(null);
  const [isImproving, setIsImproving] = useState(false);
  const [improveStatus, setImproveStatus] = useState({ timeElapsed: 0, tokensUsed: { input: 0, output: 0, total: 0 }, cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: false, model: 'deepseek-chat' }, message: '' });
  const [essayOptions, setEssayOptions] = useState({ essayType: 'narrative', targetLength: 600 });

  // JSON export/import helpers
  const fileInputRef = useRef(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonContent, setJsonContent] = useState('');

  const openJsonViewer = () => {
    try {
      const content = JSON.stringify(graphData, null, 2);
      setJsonContent(content);
      setShowJsonModal(true);
    } catch (err) {
      console.error('Failed to stringify graphData:', err);
    }
  };

  const downloadJson = () => {
    try {
      const blob = new Blob([JSON.stringify(graphData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'knowledge-graph.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download JSON:', err);
    }
  };

  const triggerImport = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (Array.isArray(data?.nodes) && Array.isArray(data?.links)) {
        setGraphData(data);
      } else if (data?.workspace && Array.isArray(data.workspace?.nodes)) {
        // Allow importing a workspace structure (from ToolBox)
        const workspace = data.workspace;
        const nodes = workspace.nodes || [];
        const links = workspace.links || [];
        setGraphData({ nodes, links });
      } else {
        console.warn('Imported JSON does not contain nodes/links arrays.');
      }
    } catch (err) {
      console.error('Failed to import JSON:', err);
    } finally {
      // Reset input so same file can be selected again later
      e.target.value = '';
    }
  };

  // === AI Generated helpers ===
  const countByGroup = (workspace) => {
    const result = { characters: 0, places: 0, objects: 0 };
    const nodes = workspace?.nodes || [];
    nodes.forEach((n) => {
      const cat = n.category || (
        ['protagonist','helper','sacrificial','community'].includes(n.type) ? 'character' :
        ['origin','destination','habitat','obstacle','dwelling','setting'].includes(n.type) ? 'place' :
        'object'
      );
      if (cat === 'character') result.characters += 1;
      else if (cat === 'place') result.places += 1;
      else result.objects += 1;
    });
    return result;
  };

  const renderPerformance = (workspace) => {
    const totals = countByGroup(workspace);
    const nodes = workspace?.nodes || [];
    const score = 100; // placeholder fixed 100 as requested
    return (
      <div className="text-sm text-blue-900 space-y-1">
        <div className="text-3xl font-bold">{score}</div>
        <div className="text-xs">分數</div>
        <div className="mt-2 text-xs">使用實體：{nodes.length}</div>
        <div className="text-xs">人物：{totals.characters}</div>
        <div className="text-xs">地點：{totals.places}</div>
        <div className="text-xs">物品：{totals.objects}</div>
      </div>
    );
  };

  // AI Suggestion apply helper
  const applySuggestion = async (suggestionText) => {
    if (!aiOutput) return;
    setIsImproving(true);
    setImproveStatus((s) => ({ ...s, message: '調用 DeepSeek 以應用建議…' }));
    // Show progress modal
    setShowStatusModal(true);
    setAnalysisStatus({
      timeElapsed: 0,
      tokensUsed: { input: 0, output: 0, total: 0 },
      cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: deepseekEssayApi.isDiscountTime?.() || false, model: 'deepseek-chat' },
      phase: 'preparing',
      message: '準備改寫…'
    });
    try {
      const baseEssay = generateEssayDraftFromWorkspace(aiOutput.workspace);
      // Build richer factual context from workspace nodes
      const nodes = aiOutput.workspace?.nodes || [];
      const factualLines = nodes.map(n => `- 名稱: ${n.name}；類型: ${n.type}${n.description ? `；描述: ${n.description}` : ''}`).join('\n');
      const prompt = `請基於以下「知識圖譜工作區」的事實資訊，寫一篇內容具體、包含人物與地點與物品細節的中文文章。\n\n【事實清單】\n${factualLines}\n\n【現有草稿】\n${baseEssay}\n\n【改寫指令】\n${suggestionText}\n\n要求：\n- 必須引用事實清單中的具體資訊（如人名、地名、物品名與描述）\n- 段落完整，故事具體，不要流於摘要或概述\n- 連貫自然，可適度創作細節但不得矛盾於事實清單。`;
      setAnalysisStatus((s)=>({ ...s, phase: 'calling_api', message: '調用 DeepSeek API 生成改寫…' }));
      const res = await deepseekEssayApi.generateEssay(prompt, { language, essayType: essayOptions.essayType, targetLength: essayOptions.targetLength, maxTokens: 1200 });
      const parsed = deepseekEssayApi.parseResponse(res);
      if (parsed.success) {
        setAiOutput((prev) => ({ ...prev, workspace: prev.workspace, essay: parsed.content }));
        const costInfo = deepseekEssayApi.calculateCost?.(res.usage?.prompt_tokens || 0, res.usage?.completion_tokens || 0, 'deepseek-chat', false) || { inputCost: 0, outputCost: 0, totalCost: 0 };
        setImproveStatus((s) => ({ ...s, message: '已應用建議', tokensUsed: res.usage || s.tokensUsed, cost: costInfo }));
        setAnalysisStatus((s)=>({
          ...s,
          phase: 'completed',
          message: '改寫完成！',
          tokensUsed: { input: res.usage?.prompt_tokens || 0, output: res.usage?.completion_tokens || 0, total: res.usage?.total_tokens || 0 },
          cost: costInfo
        }));
      } else {
        setImproveStatus((s) => ({ ...s, message: `改寫失敗：${parsed.error}` }));
        setAnalysisStatus((s)=>({ ...s, phase: 'error', message: `改寫失敗：${parsed.error}` }));
      }
    } catch (err) {
      console.error(err);
      setImproveStatus((s) => ({ ...s, message: '改寫過程出現錯誤' }));
      setAnalysisStatus((s)=>({ ...s, phase: 'error', message: '改寫過程出現錯誤' }));
    } finally {
      setIsImproving(false);
    }
  };

  const generateEssayDraftFromWorkspace = (workspace) => {
    const nodes = workspace?.nodes || [];
    const characters = nodes.filter(n => ['protagonist','helper','sacrificial','community'].includes(n.type));
    const places = nodes.filter(n => ['origin','destination','habitat','obstacle','dwelling','setting'].includes(n.type));
    const objects = nodes.filter(n => (n.category === 'object') || ['life_force','essence','legacy','bond','theme','artifact','symbol'].includes(n.type));

    const intro = characters.slice(0,2).map(n => `${n.name}（${n.type}）${n.description ? `：${n.description}` : ''}`).join('、');
    const placeLine = places.slice(0,2).map(n => `${n.name}${n.description ? `：${n.description}` : ''}`).join('、');
    const objectLine = objects.slice(0,3).map(n => `${n.name}${n.description ? `：${n.description}` : ''}`).join('、');

    const parts = [];
    if (intro) parts.push(`主要人物：${intro}。`);
    if (placeLine) parts.push(`重要場景：${placeLine}。`);
    if (objectLine) parts.push(`關鍵物件：${objectLine}。`);
    if (!parts.length) return '請在工具箱加入人物、地點與物件後再生成草稿。';
    return parts.join('\n');
  };

  const generateSummary = (workspace) => {
    const nodes = workspace?.nodes || [];
    const keyObjects = nodes.filter(n => (n.category === 'object') || ['life_force','essence','legacy','bond','theme','artifact','symbol'].includes(n.type)).slice(0,3).map(n=>n.name);
    if (keyObjects.length) {
      return `情節的核心包含 ${keyObjects.length} 個重要物件：${keyObjects.join('、')}。`;
    }
    return `所選的 ${nodes.length} 個實體，構成了可行的作文規劃。`;
  };
  
  // Floating window state
  const [floatingWindow, setFloatingWindow] = useState({
    isVisible: false,
    position: { x: 300, y: 200 },
    size: { width: 320, height: 480 }, // Doubled the height
    isDragging: false,
    isResizing: false,
    dragOffset: { x: 0, y: 0 }
  });

  // Text translations
  const texts = {
    traditional: {
      title: '知識圖譜分析器',
      subtitle: '專業視覺化與互動式旋轉控制',
      graphView: '圖形檢視',
      listView: '列表檢視',
      toolboxView: 'ToolBox',
      aiGeneratedView: 'AI Generated',
      json: 'JSON',
      import: 'Import',
      inputText: '輸入文本進行分析',
      analyzing: '分析中...',
      analyze: '分析',
      searchFilter: '搜尋與篩選',
      searchPlaceholder: '搜尋實體...',
      characters: '人物',
      places: '地點',
      objects: '物品',
      relationships: '關係',
      viewControls: '檢視控制',
      autoRotate: '自動旋轉',
      stopAutoRotate: '停止自動旋轉',
      resetAll: '重設全部',
      showLabels: '顯示標籤',
      relationshipLabels: '關係標籤',
      entityDetails: '實體詳情',
      visualization: '知識圖譜視覺化',
      dragToPan: '拖曳平移',
      clickToSelect: '點擊選擇',
      useArrows: '使用箭頭旋轉',
      selectEntity: '選擇一個實體',
      entityAnalysis: '實體分析',
      importanceScore: '重要性分數',
      statistics: '知識圖譜統計',
      textPlaceholder: '在此輸入您的文本以提取知識圖譜...',
      useMockData: '使用模擬數據',
      sampleTexts: '範例文本'
    },
    simplified: {
      title: '知识图谱分析器',
      subtitle: '专业可视化与交互式旋转控制',
      graphView: '图形视图',
      listView: '列表视图',
      toolboxView: 'ToolBox',
      aiGeneratedView: 'AI Generated',
      json: 'JSON',
      import: 'Import',
      inputText: '输入文本进行分析',
      analyzing: '分析中...',
      analyze: '分析',
      searchFilter: '搜索与筛选',
      searchPlaceholder: '搜索实体...',
      characters: '人物',
      places: '地点',
      objects: '物品',
      relationships: '关系',
      viewControls: '视图控制',
      autoRotate: '自动旋转',
      stopAutoRotate: '停止自动旋转',
      resetAll: '重置全部',
      showLabels: '显示标签',
      relationshipLabels: '关系标签',
      entityDetails: '实体详情',
      visualization: '知识图谱可视化',
      dragToPan: '拖拽平移',
      clickToSelect: '点击选择',
      useArrows: '使用箭头旋转',
      selectEntity: '选择一个实体',
      entityAnalysis: '实体分析',
      importanceScore: '重要性分数',
      statistics: '知识图谱统计',
      textPlaceholder: '在此输入您的文本以提取知识图谱...',
      useMockData: '使用模拟数据',
      sampleTexts: '示例文本'
    }
  };

  const t = texts[language];

  // Load sample texts via A2A (with Supabase fallback)
  useEffect(() => {
    const loadSamples = async () => {
      setLoadingSamples(true);
      try {
        const samples = await chineseDataService.getAllSampleTexts();
        setSampleTexts(samples);
        
        // Set default input text if available
        if (samples.sampleTexts && samples.sampleTexts[selectedSample]) {
          setInputText(samples.sampleTexts[selectedSample]);
        }
      } catch (error) {
        // Only log if it's a real error (both A2A and Supabase failed)
        // Connection errors are already handled by the service layer with fallback
        console.error('[KnowledgeGraph] Error loading KG data (both A2A and fallback failed):', error);
        // Set empty state so UI doesn't break
        setSampleTexts({ sampleList: [], sampleTexts: {} });
      } finally {
        setLoadingSamples(false);
      }
    };
    
    loadSamples();
  }, []);

  // Sample text options
  const sampleTextOptions = (sampleTexts.sampleList || []).map(item => ({
    id: item.id,
    name: item.name,
    text: sampleTexts.sampleTexts[item.id] || ''
  }));

  // Auto-rotation effect
  useEffect(() => {
    if (isAutoRotating) {
      autoRotateRef.current = setInterval(() => {
        setRotation(prev => ({
          ...prev,
          z: (prev.z + 1) % 360
        }));
      }, 50);
    } else {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
      }
    }

    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
      }
    };
  }, [isAutoRotating]);

  // Initialize with mock data via A2A (with Supabase fallback)
  useEffect(() => {
    if (useMockData && selectedSample) {
      const loadMockData = async () => {
        try {
          console.log('[KnowledgeGraph] Loading mock data for:', selectedSample);
          const mockData = await chineseDataService.getMockDataByStoryId(selectedSample);
          console.log('[KnowledgeGraph] Received mock data:', {
            hasMockData: !!mockData,
            hasNodes: !!(mockData && mockData.nodes),
            hasLinks: !!(mockData && mockData.links),
            nodesCount: mockData && mockData.nodes ? mockData.nodes.length : 0,
            linksCount: mockData && mockData.links ? mockData.links.length : 0,
            sampleNodes: mockData && mockData.nodes ? mockData.nodes.slice(0, 2) : []
          });
          
          if (mockData) {
            // Transform to component format
            const newGraphData = {
              nodes: mockData.nodes || [],
              links: mockData.links || []
            };
            console.log('[KnowledgeGraph] Setting graph data:', {
              nodesCount: newGraphData.nodes.length,
              linksCount: newGraphData.links.length
            });
            setGraphData(newGraphData);
          } else {
            console.warn('[KnowledgeGraph] No mock data returned for:', selectedSample);
          }
        } catch (error) {
          console.error('[KnowledgeGraph] Error loading mock data:', error);
        }
      };
      loadMockData();
    }
  }, [useMockData, selectedSample]);

  // Extract entities and relationships from Chinese text using DeepSeek API
  const extractChineseKnowledgeGraph = useCallback(async (text) => {
    setIsAnalyzing(true);
    setShowStatusModal(true);
    
    try {
      if (useMockData) {
        // Use mock data for demonstration with status simulation
        const mockStatusUpdate = (status) => {
          setAnalysisStatus(status);
        };
        
        // Simulate analysis phases
        mockStatusUpdate({
          timeElapsed: 0,
          tokensUsed: { input: 0, output: 0, total: 0 },
          cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: false, model: 'deepseek-chat' },
          phase: 'preparing',
          message: '準備分析...'
        });
        
        setTimeout(() => {
          mockStatusUpdate({
            timeElapsed: 500,
            tokensUsed: { input: 0, output: 0, total: 0 },
            cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: false, model: 'deepseek-chat' },
            phase: 'creating_prompt',
            message: '創建分析提示...'
          });
        }, 200);
        
        setTimeout(() => {
          mockStatusUpdate({
            timeElapsed: 1000,
            tokensUsed: { input: 0, output: 0, total: 0 },
            cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: false, model: 'deepseek-chat' },
            phase: 'calling_api',
            message: '調用 DeepSeek API...'
          });
        }, 500);
        
        setTimeout(async () => {
          try {
            const mockData = await chineseDataService.getMockDataByStoryId(selectedSample);
            if (mockData) {
              setGraphData({
                nodes: mockData.nodes || [],
                links: mockData.links || []
              });
            }
            setIsAnalyzing(false);
            
            mockStatusUpdate({
              timeElapsed: 1500,
              tokensUsed: { input: 0, output: 0, total: 0 },
              cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: false, model: 'deepseek-chat' },
              phase: 'completed',
              message: '分析完成！'
            });
          } catch (error) {
            console.error('Error loading mock data:', error);
            setIsAnalyzing(false);
          }
        }, 1500);
      } else {
        // Use A2A backend for knowledge graph analysis
        const result = await a2aChineseService.analyzeKnowledgeGraph(text, {
          extractEntities: true,
          extractRelations: true,
          language: language
        });
        
        // Transform A2A result to match expected format
        const graphData = {
          nodes: result.nodes || result.graph?.nodes || [],
          links: result.links || result.graph?.links || [],
          entities: result.entities || [],
          relationships: result.relationships || [],
          summary: result.summary || result.analysis || {}
        };
        
        setGraphData(graphData);
        setIsAnalyzing(false);
      }
          } catch (error) {
        console.error('Error analyzing knowledge graph:', error);
        setIsAnalyzing(false);
        
        // The API service now handles errors internally and returns fallback data
        // So we shouldn't reach this catch block for API parsing errors
        setAnalysisStatus(prev => ({
          ...prev,
          phase: 'error',
          message: `分析失敗: ${error.message}`
        }));
        
        // Fallback to mock data on error
        try {
          const mockData = await chineseDataService.getMockDataByStoryId('moonAndRabbit');
          if (mockData) {
            setGraphData({
              nodes: mockData.nodes || [],
              links: mockData.links || []
            });
          }
        } catch (fallbackError) {
          console.error('Error loading fallback mock data:', fallbackError);
        }
      }
  }, [useMockData, selectedSample, language]);

  // Handle entity selection and show floating window
  const handleEntityClick = (entity) => {
    setSelectedEntity(entity);
    setFloatingWindow(prev => ({
      ...prev,
      isVisible: true
    }));
  };

  const allEntities = [...(graphData.nodes || [])];
  const filteredEntities = allEntities.filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entity.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
            <p className="text-gray-600">{t.subtitle}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">核心概念</h3>
            </div>
            <p className="text-sm text-purple-700">掌握寫作的基本概念和術語</p>
          </div>
          
          <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Network className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-indigo-900">概念關係</h3>
            </div>
            <p className="text-sm text-indigo-700">理解寫作概念之間的關聯性</p>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Target className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">應用實例</h3>
            </div>
            <p className="text-sm text-blue-700">通過實例學習寫作技巧</p>
          </div>
        </div>
      </div>

      {/* Text Input Section */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t.inputText}</h2>
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setLanguage('traditional')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  language === 'traditional' 
                    ? 'bg-white text-red-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                繁體
              </button>
              <button
                onClick={() => setLanguage('simplified')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  language === 'simplified' 
                    ? 'bg-white text-red-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                简体
              </button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('graph')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'graph' 
                    ? 'bg-white text-red-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Network className="w-4 h-4 inline mr-2" />
                {t.graphView}
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-red-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid className="w-4 h-4 inline mr-2" />
                {t.listView}
              </button>
              <button
                onClick={() => setViewMode('toolbox')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'toolbox' 
                    ? 'bg-white text-red-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.toolboxView}
              </button>
              <button
                onClick={() => setViewMode('ai')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'ai' 
                    ? 'bg-white text-red-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.aiGeneratedView}
              </button>
              {/* JSON and Import buttons */}
              <button
                onClick={openJsonViewer}
                className="px-3 py-2 rounded-md text-sm font-medium transition-colors bg-white text-gray-700 hover:text-gray-900 shadow-sm"
                title="View current graph JSON"
              >
                <Download className="w-4 h-4 inline mr-1" />{t.json}
              </button>
              <button
                onClick={triggerImport}
                className="px-3 py-2 rounded-md text-sm font-medium transition-colors bg-white text-gray-700 hover:text-gray-900 shadow-sm"
                title="Import graph JSON"
              >
                <Upload className="w-4 h-4 inline mr-1" />{t.import}
              </button>
            </div>

            <button
              onClick={() => extractChineseKnowledgeGraph(inputText)}
              disabled={isAnalyzing}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {isAnalyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t.analyzing}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  {t.analyze}
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Sample Text Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t.sampleTexts}</label>
          <select
            value={selectedSample}
            onChange={(e) => {
              setSelectedSample(e.target.value);
              const selected = sampleTextOptions.find(s => s.id === e.target.value);
              if (selected) {
                setInputText(selected.text);
              }
            }}
            disabled={loadingSamples}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
          >
            {loadingSamples ? (
              <option>載入範例中...</option>
            ) : (
              sampleTextOptions.map(sample => (
                <option key={sample.id} value={sample.id}>{sample.name}</option>
              ))
            )}
          </select>
        </div>

        {/* Mock Data Toggle */}
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={useMockData}
              onChange={(e) => setUseMockData(e.target.checked)}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="ml-2 text-sm text-gray-700">{t.useMockData}</span>
          </label>
        </div>
        
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
          placeholder={t.textPlaceholder}
        />
        {/* Hidden file input for Import */}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={handleImportFileChange}
        />
      </div>

      {/* Graph Visualization / List / Toolbox / AI tabs */}
      {viewMode === 'graph' ? (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Graph Controls */}
          <div className="xl:col-span-1 space-y-4">
            {/* Search */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t.searchFilter}</h3>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Main Graph Visualization */}
          <div className="xl:col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t.visualization}</h3>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Hand className="w-4 h-4" />
                    <span>{t.dragToPan}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MousePointer2 className="w-4 h-4" />
                    <span>{t.clickToSelect}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Rotate3D className="w-4 h-4" />
                    <span>{t.useArrows}</span>
                  </div>
                </div>
              </div>
              
                             <KnowledgeGraphVisualization
                 graphData={graphData}
                 selectedEntity={selectedEntity}
                 onEntityClick={handleEntityClick}
                 graphSettings={graphSettings}
                 filterSettings={filterSettings}
                 onFilterChange={handleFilterChange}
                 onGraphSettingChange={handleGraphSettingChange}
                 rotation={rotation}
                 setRotation={setRotation}
                 zoom={zoom}
                 setZoom={setZoom}
                 translation={translation}
                 setTranslation={setTranslation}
                 isAutoRotating={isAutoRotating}
                 setIsAutoRotating={setIsAutoRotating}
                 isDragging={isDragging}
                 setIsDragging={setIsDragging}
                 dragStart={dragStart}
                 setDragStart={setDragStart}
                 t={t}
               />
            </div>
          </div>
        </div>
      ) : viewMode === 'list' ? (
        /* List View Mode */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Entity Categories */}
          <div className="lg:col-span-2 space-y-6">
            {[
              { key: 'characters', types: ['protagonist', 'helper', 'sacrificial', 'community'], icon: Users },
              { key: 'places', types: ['origin', 'destination', 'habitat', 'obstacle', 'dwelling', 'setting'], icon: MapPin },
              { key: 'objects', types: ['life_force', 'essence', 'legacy', 'bond', 'theme', 'artifact', 'symbol'], icon: Sparkles }
            ].map(category => {
              const categoryEntities = filteredEntities.filter(entity => 
                category.types.includes(entity.type)
              );

              if (categoryEntities.length === 0) return null;

              const Icon = category.icon;

              return (
                <div key={category.key} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    {t[category.key]}
                    <span className="ml-2 text-xs text-gray-500">({categoryEntities.length})</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {categoryEntities.map((entity) => (
                      <div
                        key={entity.id}
                        onClick={() => handleEntityClick(entity)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                          selectedEntity?.id === entity.id
                            ? 'ring-2 ring-red-500 ring-offset-2 border-red-200 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <h4 className="font-semibold text-sm">{entity.name}</h4>
                        <p className="text-xs mt-1 text-gray-600">{entity.description}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="inline-block px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                            {entity.type}
                          </span>
                          <span className="text-xs text-gray-500">
                            分數: {entity.importance}/10
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Entity Details */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {selectedEntity ? t.entityAnalysis : t.selectEntity}
            </h3>
            
            {selectedEntity ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <h4 className="font-bold text-lg">{selectedEntity.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{selectedEntity.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="inline-block px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                      {selectedEntity.type}
                    </span>
                    <span className="text-sm text-gray-500">
                      {t.importanceScore}: {selectedEntity.importance}/10
                    </span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">{t.relationships}</h4>
                  <div className="space-y-2">
                    {graphData.links
                      .filter(link => link.source === selectedEntity.id || link.target === selectedEntity.id)
                      .map((relation, index) => {
                        const relatedId = relation.source === selectedEntity.id ? relation.target : relation.source;
                        const relatedEntity = allEntities.find(e => e.id === relatedId);
                        const isOutgoing = relation.source === selectedEntity.id;

                        return (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium">
                                {isOutgoing ? selectedEntity.name : relatedEntity?.name}
                              </span>
                              <span className="text-gray-500 italic">{relation.type}</span>
                              <span className="font-medium">
                                {isOutgoing ? relatedEntity?.name : selectedEntity.name}
                              </span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-xs text-gray-600">{relatedEntity?.description}</p>
                              <span className="text-xs text-gray-500">
                                強度: {relation.strength}/10
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>點擊任何實體以查看詳細分析和關係</p>
              </div>
            )}
          </div>
        </div>
      ) : viewMode === 'toolbox' ? (
        <KnowledgeGraphToolbox
          graphData={graphData}
          language={language}
          onRun={(workspaceGraph) => {
            // When RUN, serialize to aiGenerated state and open AI tab
            setAiOutput({
              time: new Date().toISOString(),
              workspace: workspaceGraph,
            });
            setViewMode('ai');
          }}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-bold">AI</div>
            <h3 className="text-xl font-semibold text-gray-900">AI 生成分析</h3>
          </div>

          {aiOutput ? (
            <>
              {/* Essay draft */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2">生成的作文草稿</h4>
                  {isImproving && (
                    <div className="mb-3 text-xs text-green-800 bg-green-100 border border-green-200 rounded px-3 py-2">
                      <div>請稍候，AI 正在應用建議…</div>
                      {improveStatus.message && <div className="mt-1">{improveStatus.message}</div>}
                    </div>
                  )}
                  <p className="text-green-900 text-sm leading-6 whitespace-pre-wrap">
                    {aiOutput.essay || generateEssayDraftFromWorkspace(aiOutput.workspace)}
                  </p>
                </div>
                </div>
                <div>
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">表現反饋</h4>
                    {renderPerformance(aiOutput.workspace)}
                  </div>
                </div>
              </div>

              {/* Summary and recommendations */}
              <div className="mt-6 p-4 rounded-lg bg-purple-50 border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-2">分析摘要</h4>
                <p className="text-purple-900 text-sm">
                  {generateSummary(aiOutput.workspace)}
                </p>
              </div>

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-200">
                  <h4 className="font-semibold text-indigo-900 mb-3">關鍵洞察</h4>
                  <ul className="text-indigo-900 text-sm list-disc pl-5 space-y-1">
                    <li>{`所選擇的 ${aiOutput.workspace?.nodes?.length || 0} 個實體，形成了一個較為均衡的敘事結構。`}</li>
                  </ul>
                </div>
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <h4 className="font-semibold text-emerald-900 mb-3">建議</h4>
                  <ul className="text-emerald-900 text-sm pl-0 space-y-2">
                    {[
                      '可增加人物以豐富互動。',
                      '加入更多多樣化的地點以擴展世界觀。',
                      '建立實體之間的關聯以增強敘事連貫性。',
                      '思考這些實體如何互動以塑造更有張力的故事線。'
                    ].map((sug, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="leading-6">• {sug}</span>
                        <div className="ml-2 flex items-center gap-2">
                          <button
                            onClick={() => applySuggestion(sug)}
                            className="px-2 py-0.5 text-xs rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={isImproving}
                            title="套用此建議並改寫草稿"
                          >
                            AI 套用
                          </button>
                          {/* Quick user controls (placeholder; future wiring if needed) */}
                          <select className="text-xs border rounded px-1 py-0.5" defaultValue="narrative">
                            <option value="narrative">記敘文</option>
                            <option value="argumentative">議論文</option>
                            <option value="descriptive">描寫文</option>
                            <option value="letter">書信</option>
                          </select>
                          <input type="number" min={100} max={1500} step={50} placeholder="字數" className="w-16 text-xs border rounded px-1 py-0.5" />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <div className="text-gray-500">尚未生成。請於「工具箱」設計後按「執行」。</div>
          )}
        </div>
      )}

      {/* Floating Entity Details Window */}
      <EntityDetailsWindow
        isVisible={floatingWindow.isVisible}
        selectedEntity={selectedEntity}
        graphData={graphData}
        floatingWindow={floatingWindow}
        setFloatingWindow={setFloatingWindow}
        onClose={() => setFloatingWindow(prev => ({ ...prev, isVisible: false }))}
      />

      {/* Statistics Dashboard */}
      <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.statistics}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {graphData.nodes.filter(n => ['protagonist', 'helper', 'sacrificial', 'community'].includes(n.type)).length}
            </div>
            <div className="text-sm text-gray-600">{t.characters}</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {graphData.nodes.filter(n => ['origin', 'destination', 'habitat', 'obstacle', 'dwelling', 'setting'].includes(n.type)).length}
            </div>
            <div className="text-sm text-gray-600">{t.places}</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {graphData.nodes.filter(n => ['life_force', 'essence', 'legacy', 'bond', 'theme','artifact','symbol'].includes(n.type)).length}
            </div>
            <div className="text-sm text-gray-600">{t.objects}</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{graphData.links.length}</div>
            <div className="text-sm text-gray-600">{t.relationships}</div>
          </div>
        </div>
      </div>

      {/* JSON Viewer Modal */}
      {showJsonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowJsonModal(false)} />
          <div className="relative bg-white w-[90vw] max-w-3xl h-[70vh] rounded-lg shadow-xl border border-gray-200 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Graph JSON</h4>
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadJson}
                  className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200"
                >
                  下載
                </button>
                <button
                  onClick={() => setShowJsonModal(false)}
                  className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                >
                  關閉
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <pre className="text-xs leading-5 whitespace-pre-wrap bg-gray-50 p-3 rounded border border-gray-200">{jsonContent}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Status Modal */}
      <AnalysisStatusModal
        isVisible={showStatusModal}
        status={analysisStatus}
        onClose={() => setShowStatusModal(false)}
      />
    </div>
  );
};

export default KnowledgeGraph; 